// client/src/lib/prefetch.ts
//
// Single source of truth for the four list queries the Home board is built from.
//
// These used to be written out twice — here and again in AppDashboard.loadBoard —
// kept in sync only by a comment saying "same fetches, same limits". That meant a
// page load fired all four (to warm the cache), the dashboard mounted and fired the
// same four AGAIN, and the two sets raced each other for the browser's 6-connection
// budget and the same Mongo queries. The prefetch only writes the cache once ALL
// four resolve, so while it was in flight the board could read nothing from it — it
// just doubled the load it was supposed to save.
//
// fetchBoardData() collapses that: concurrent callers share one in-flight promise.
import { fetchRfqList } from './rfqApi';
import { fetchQuotationList } from './quotationApi';
import { fetchConfirmedOrders } from './confirmedOrderApi';
import { fetchVendorPurchaseOrders } from './vendorPurchaseOrderApi';
import { writePageCache, CACHE_KEYS } from './pageCache';

/** Shared in-flight board load, or null when idle. */
let inFlight: Promise<any[]> | null = null;

/**
 * The four list responses the board derives its state from, in the order
 * applyBoardData expects: [rfqs, confirmedOrders, quotations, vendorPurchaseOrders].
 *
 * Callers arriving while a load is already running join that one instead of starting
 * a second. Rejects if the RFQ list fails (the board can't render without it); the
 * other three degrade to null, as before.
 */
export function fetchBoardData(): Promise<any[]> {
  if (inFlight) return inFlight;

  inFlight = Promise.all([
    fetchRfqList('all', 1, 200),
    // The board only reads header/link fields from these — request them lean
    // (no per-order items[]) so the dashboard isn't dragging hundreds of item
    // arrays it never renders.
    fetchConfirmedOrders(1, 300, '', true).catch(() => null),
    fetchQuotationList({ page: 1, limit: 500 }).catch(() => null),
    fetchVendorPurchaseOrders(1, 500, '', true).catch(() => null),
  ])
    .then((results) => {
      writePageCache(CACHE_KEYS.board, results); // warm the cache for instant next visit
      return results;
    })
    .finally(() => {
      // Cleared regardless of outcome so a failed load doesn't poison later attempts —
      // the Refresh button and the next mount must be able to retry.
      inFlight = null;
    });

  return inFlight;
}

/**
 * Warm the board cache right after login / on session restore, so arriving at the
 * dashboard is instant. Fire-and-forget: any failure is ignored and the page just
 * fetches normally.
 */
export function prefetchMainPages(): void {
  fetchBoardData().catch(() => {
    /* ignore — pages fall back to fetching normally */
  });
}
